import { Button, DateRangePicker, DropDown, EmptyState, SearchTable, Table, Tag, Toast, Tooltip } from "@kaleyra/emmy";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Styles from './index.module.scss';
import optionIcon from '../../../../assets/optionIcon.svg';
import { DownloadReport } from '../../../../interface/urlShortener';
import { dateToStringFormat, checkKaleyraExpertRole, checkViewOnlyPrivileges } from '../../../../utils';
import isEmpty from "lodash/isEmpty";
import cn from "classnames";
import { debounce } from "lodash";
import moment from "moment";
import PlusIcon from '../../../../assets/plusIcon.svg';
import rightArrowIcon from '../../../../assets/right-arrow1.svg';
import BulkUploadFilter from "../bulkUploadFilter";
import { Link } from "react-router-dom";

export type DispatchPropsType = {
    onAddUrl: () => void;
    onEditUrl: (id: string, appid: string) => void;
    onDelete: (id: string, appid: string, filename: string) => void;
    onDownloadReportAll: (callback: (resp: DownloadReport, flag: boolean) => void) => void;
    onDownloadReportById: (id: string, callback: (resp: DownloadReport, flag: boolean) => void) => void;
    onLoad: () => void;
    onPaginate: () => void;
    onSearchStrChange: (searchStr?: string) => void;
    onFilter: (filters: Record<string, any> | undefined) => void;
};

export type MapStateType = {
    data: any;
    downloadResponse: DownloadReport;
    inProgress: boolean;
    total: number;
    page: number;
};

export const REFERENCE_MAP_KEY = 'InderectReferenceMap'

const generateUniqueUUID = (map: Map<string, string>) => {
    let uuid = '';
    const hexDigits = '0123456789abcdef';
    const existingKeys = Array.from(map.keys());
    do {
      uuid = '';
      for (let i = 0; i < 36; i++) {
        if (i === 8 || i === 13 || i === 18 || i === 23) {
          uuid += '-';
        } else if (i === 14) {
          uuid += '4';
        } else {
          uuid += hexDigits.charAt(Math.floor(Math.random() * hexDigits.length));
        }
      }
    } while (existingKeys.includes(uuid));
    return uuid;
}

const getIndirectReferenceMapping = (): Map<string, string> => {
    try {
        const storedMapData = localStorage.getItem(REFERENCE_MAP_KEY);
        const storedMap = storedMapData ? new Map<string, string>(JSON.parse(storedMapData)) : new Map<string, string>();
        return storedMap;
    } catch (error) {
        console.error('Error parsing idirect reference stored map for Bulk URL Shortner:', error);
        localStorage.removeItem(REFERENCE_MAP_KEY);
        return new Map<string, string>();
    }
}

const getReferencedValueKey = (value: string, map: Map<string, string>) => {
    const entry = Array.from(getIndirectReferenceMapping().entries()).find(([key, val]) => val === value);
    return entry ? entry[0] : null;
}

export const ReferecedValueFor = (key: string): string | undefined => {
    return getIndirectReferenceMapping().get(key);
}

const retriveReference = (referencedValue: string) => {
    const indirectReferenceMap = getIndirectReferenceMapping();
    const existingKey = getReferencedValueKey(referencedValue, indirectReferenceMap) ;
    if (existingKey == null) {
        const key = generateUniqueUUID(indirectReferenceMap);
        indirectReferenceMap.set(key, referencedValue);
        localStorage.setItem(REFERENCE_MAP_KEY, JSON.stringify(Array.from(indirectReferenceMap)));
        return key;
    } else {
        return existingKey;
    }
}

const BulkUrlShortener: React.FC<MapStateType & DispatchPropsType> = ({
    data,
    onAddUrl,
    onDelete,
    onLoad,
    onPaginate,
    total,
    page,
    inProgress,
    onFilter,
    onSearchStrChange,
    onDownloadReportAll,
    onDownloadReportById }) => {

    const { t } = useTranslation();
    const [searchText, setSearchText] = useState('');
    const [selFilters, setSelFilters] = useState<any>({});
    const [appliedFilterText, setAppliedFilterText] = useState<Record<string, string>>({} as Record<string, string>);

    useEffect(() => {
        onLoad();
    }, []);

    const debouncedChange = React.useCallback(
        debounce((value: any = "") => {
            setSearchText(value);
            onSearchStrChange(value);
        }, 400),
        []
    );

    const mapStatusOptions:Record<string,string> = {
        '1': t('mainCompleted'),
        '2': t('KLR01815'),
        '3': t('mainFailed'),
        '0': t('mainPending')
    }

    const getDropDown = (id: string, appid: string, status: string | number, filename: string): React.ReactNode => {
        let items: any = [];
        let downloadOption = {
            label: `${t('BLD02027')}`,
            value: 'download',
            action: () => onDownloadReportById(filename, downloadCallback)
        };
        let deleteOption = {
            label: 'Delete',
            value: 'delete',
            action: () => onDelete(id, appid, filename),
        }
        if (status.toString() !== "2" && !checkKaleyraExpertRole()) {
            items.push(deleteOption);
        }
        if (status.toString() === "1") {
            items = [downloadOption, ...items];
        }
        return (
            items.length ? <DropDown
                items={items}
                placement="bottom"
                trigger={['hover']}
                className={Styles.dropdown}>
                <img src={optionIcon} alt="options" />
            </DropDown> : <></>
        )
    }

    const downloadCallback = (response: DownloadReport, flag: boolean) => {
        if (response && flag && !isEmpty(response.data)) {
            window.open(response.data.s3_url);
            Toast(response.message, "success");
        } else {
            Toast(response.data?.message, "error")
        }
    }

    const clearFilters = () => {
        setAppliedFilterText({});
        setSelFilters({});
        onFilter({});
    }

    const renderStatus = (statusId: string) => {
        switch (statusId.toString()) {
            case "0":
                return { type: "draft", label: "Pending" };
            case "2":
                return { type: "warning", label: "In Progress" };
            case "1":
                return { type: "success", label: "Completed" };
            case "3":
                return { type: "error", label: "Failed" };
            default:
                return { type: "draft", label: "No request" };
        }
    }

    const updateFilters = (filters: any, remove?: boolean) => {
        let appliedOb = {};
        const { status, created_at } = filters;

        if(status){
            appliedOb = {
               ...appliedOb,
                status: remove ? selFilters.status : `${mapStatusOptions[status]}`
            }
        }

        if (created_at) {
            let createdArr = created_at.split(",");
            const [start, end] = createdArr;
            const startDate = moment(start).format('DD MMM YYYY');
            const endDate = moment(end).format('DD MMM YYYY');
            appliedOb = {
                ...appliedOb,
                created_at: remove ? selFilters.created_at : `${t('KLR00112')} ${startDate} ${t('KLR00113')} ${endDate}`
            }
        }
        setAppliedFilterText( remove ? filters : appliedOb);
        onFilter(remove ? appliedOb : filters);
        setSelFilters(remove ? appliedOb : filters);
    }

    const getDataTable = () => {
        return (<div className={Styles.container}>
            <SearchTable.SearchTable
                manual
                enableSearch
                rowClassName={Styles.row}
                onPaginate={onPaginate}
                total={total}
                page={page + 1}
                inProgress={inProgress}
                pageSize={25}
                scrollableTarget={'modalContainer'}
                onFilter={(e: any, remove?: boolean) => {
                    updateFilters(e, remove);
                }}
                onSearch={(val: any) => {
                    debouncedChange(val);
                }}
                filters={appliedFilterText}
                clearAllFilters={clearFilters}
                searchText={searchText}
                rightItems={[
                    !checkViewOnlyPrivileges("urlshortner","bulk") ? <Button type="primary" iconPosition="left" icon={<img src={PlusIcon} />} onClick={onAddUrl}>{t('BLD02019')}</Button> : <></>
                ]}
                filterComponents={(filtersOb, tableOnFilter) => (
                    <BulkUploadFilter appliedFilters={selFilters || {}} onFilter={tableOnFilter} />
                )}
                data={data}
                columns={[
                    {
                        accessor: 'title',
                        header: `${t('mainTitle')}`,
                        Cell: (({ original }) => {
                            return (
                                <Tooltip
                                    placement="top"
                                    title={original.title}
                                    trigger={[
                                        'hover',
                                    ]}
                                >
                                    <div className={cn(Styles.rowWrapper, Styles.titleWrapper)}>{original.title || "-"}</div>
                                </Tooltip>
                            )
                        })
                    },
                    {
                        accessor: 'created',
                        header: `${t('mainCreatedOn')}`,
                        Cell: (({ original }) => {
                            return dateToStringFormat(new Date(original.created))
                        })
                    },
                    {
                        accessor: 'status',
                        header: `${t('status')}`,
                        Cell: (({ original }: any) => {
                            return (<Tag label={renderStatus(original.status).label} type={renderStatus(original.status).type} />)
                        }
                        )
                    },
                    {
                        accessor: '',
                        Cell: (({ original }) => getDropDown(original.id, original.appid, original.status, original.filename)),
                    },
                    {
                        accessor: '',
                        Cell: (({ original }) => {
                            const referenceID = retriveReference(original.appid)
                            return <Link to={`/URL-shortener/bulk-upload/details?bulk_id=${referenceID}`}><img src={rightArrowIcon} alt="arrow" /></Link>
                        })
                    }
                ]}
            />
        </div>)
    }

    return (
        (!isEmpty(data) && !isEmpty(data) && !inProgress && data.length === 0) ?
            <div className={Styles.noDataContainer}>
                <EmptyState wraperStyle={{ width: '24rem' }}
                    rightSideWrapperStyle={{ width: '100%' }}
                    mainHeadStyle={{ fontSize: '1.25rem', fontWeight: 600, lineHeight: '2rem' }}
                    bordered={true} showButton={false} headMsg={t('tabURLShortener')}
                    subHeadMsg={t('noUrlMessage')} />
            </div> : getDataTable())
}

export default BulkUrlShortener;